import React, { useState, useEffect, useRef, useCallback } from 'react';
import { appVersion } from '../services/appVersion.mjs';
import { api } from '../services/api';
import { sipService } from '../services/sip';
import { AudioTunnel, sasToEmoji } from '../services/tunnel';
import { FONT, colors, radii, shadows } from '../theme';
import { Diamond, Shield, ShieldCheck, ShieldAlert, Lock, Phone, Mic, MicOff, Volume2, Volume1, X, Clock, Check } from 'lucide-react';

const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  REGISTERING: 'registering',
  WAITING: 'waiting',
  VERIFYING: 'verifying',
  ACTIVE: 'active',
  ENDED: 'ended',
};

const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || 'dev';

export default function JoinPage({ code }) {
  // Asked at render, never captured at import: the shell injects its
  // bridge at document start, and a module-level constant can run first —
  // which is how the screen said «web» inside the app (03.09.2026).
  const APP_VERSION = appVersion();
  const [linkInfo, setLinkInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [encryption, setEncryption] = useState(null);
  const [verifySteps, setVerifySteps] = useState([]);
  const [speaker, setSpeaker] = useState(false);
  const [sasEmoji, setSasEmoji] = useState(null);
  const [sasVerified, setSasVerified] = useState(null);
  const [authState, setAuthState] = useState(null); // ADR-014 identity-key verdict
  const [peerSasVerified, setPeerSasVerified] = useState(null);
  const [mySasChoice, setMySasChoice] = useState(null);
  const audioRef = useRef();
  const remoteStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const timerRef = useRef();
  const mountedRef = useRef(true);
  const joinedRef = useRef(false);
  const tunnelRef = useRef(null);
  const nativeCallActiveRef = useRef(false);
  const isNative = typeof window !== 'undefined' && !!window.VoxioxNative;

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Native iOS bridge: proximity & audio route callbacks
  useEffect(() => {
    if (!isNative) return;
    window.VoxioxBridge = window.VoxioxBridge || {};
    window.VoxioxBridge.onProximityChange = (isNear) => {
      setSpeaker(!isNear);
    };
    window.VoxioxBridge.onAudioRouteChange = (route) => {
      setSpeaker(route === 'speaker');
    };
    return () => {
      if (nativeCallActiveRef.current && window.VoxioxNative) {
        window.VoxioxNative.callEnded();
        nativeCallActiveRef.current = false;
      }
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onProximityChange;
        delete window.VoxioxBridge.onAudioRouteChange;
      }
    };
  }, []); // eslint-disable-line

  // ─── Resolve link on mount ──────────────────────────────────────────────────
  useEffect(() => {
    api.resolveLink(code)
      .then((info) => {
        if (!mountedRef.current) return;
        setLinkInfo(info);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        if (err.reason === 'expired') {
          setError('Срок действия ссылки истёк. Запросите новую ссылку у создателя.');
        } else if (err.reason === 'deleted') {
          setError('Ссылка больше недоступна. Запросите новую ссылку у создателя.');
        } else if (err.reason === 'max_uses') {
          setError('Ссылка использована максимальное число раз.');
        } else {
          setError('Ссылка недействительна или срок действия истёк');
        }
        setLoading(false);
      });
  }, [code]);

  // ─── Auto-join when link info is ready ──────────────────────────────────────
  useEffect(() => {
    if (!linkInfo || joinedRef.current) return;
    joinedRef.current = true;
    joinCall();
  }, [linkInfo]); // eslint-disable-line

  const startTimer = useCallback(() => {
    const start = Date.now();
    timerRef.current = setInterval(() => {
      if (mountedRef.current) setDuration(Math.floor((Date.now() - start) / 1000));
    }, 1000);
  }, []);

  const endCall = useCallback(() => {
    clearInterval(timerRef.current);
    if (tunnelRef.current) {
      try { tunnelRef.current.leave(); } catch (e) {}
      tunnelRef.current = null;
    }
    sipService.hangup();
    sipService.disconnect();
    // Cleanup AudioContext
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.disconnect(); } catch (e) {}
      sourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (e) {}
      audioContextRef.current = null;
    }
    // Native iOS: end call (audio route, wake lock, proximity)
    if (nativeCallActiveRef.current && window.VoxioxNative) {
      window.VoxioxNative.callEnded();
      nativeCallActiveRef.current = false;
    }
    // Native iOS: dismiss CallKit call screen
    if (window.VoxioxNative?.endCallKit) {
      window.VoxioxNative.endCallKit();
    }
    if (mountedRef.current) setCallState(CALL_STATES.ENDED);
  }, []);

  // ─── SAS verification ──────────────────────────────────────────────────────
  useEffect(() => {
    if (mySasChoice === false || peerSasVerified === false) {
      setSasVerified(false);
    } else if (mySasChoice === true && peerSasVerified === true) {
      setSasVerified(true);
    }
  }, [mySasChoice, peerSasVerified]);

  const handleSasConfirm = () => {
    setMySasChoice(true);
    tunnelRef.current?.confirmPeerIdentity?.();
    if (tunnelRef.current) tunnelRef.current.sendSasVerify(true);
  };

  const handleSasDeny = () => {
    setMySasChoice(false);
    if (tunnelRef.current) tunnelRef.current.sendSasVerify(false);
  };

  // ─── Encryption verification ────────────────────────────────────────────────
  const verifyEncryption = useCallback(async () => {
    if (!mountedRef.current) return;
    setCallState(CALL_STATES.VERIFYING);

    const steps = [
      { label: 'Установка DTLS соединения...', done: false },
      { label: 'Проверка сертификата...', done: false },
      { label: 'Шифрование SRTP активировано', done: false },
    ];
    setVerifySteps([...steps]);

    // Step 1: DTLS
    await delay(600);
    steps[0].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);

    // Step 2: Certificate check
    await delay(500);
    let fingerprint = null;
    try {
      const pc = sipService.getConnection();
      if (pc) {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'certificate' && report.fingerprint) {
            fingerprint = report.fingerprint;
          }
        });
      }
    } catch (e) { /* ignore stats errors */ }

    steps[1].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);

    // Step 3: SRTP active
    await delay(400);
    steps[2].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);
    setEncryption({ verified: true, fingerprint });

    await delay(300);
    if (mountedRef.current) setCallState(CALL_STATES.ACTIVE);
  }, []);

  // ─── Main join flow ─────────────────────────────────────────────────────────
  const joinCall = async () => {
    setCallState(CALL_STATES.CONNECTING);
    setError('');

    // Native iOS: start call (earpiece mode, wake lock, proximity sensor)
    if (isNative && !nativeCallActiveRef.current) {
      window.VoxioxNative.callStarted();
      nativeCallActiveRef.current = true;
    }

    try {
      const creds = await api.getGuestCreds(code);
      if (!mountedRef.current) return;

      // ─── Tunnel mode (E2EE) ─────────────────────────────────────
      if (creds.tunnel && creds.tunnel_token) {
        setCallState(CALL_STATES.REGISTERING);
        const tunnel = new AudioTunnel();
        tunnelRef.current = tunnel;

        tunnel.onAuthState = (state) => setAuthState(state);
        tunnel.onPeerJoined = (sasBytes) => {
          if (!mountedRef.current) return;
          startTimer();
          setSasEmoji(sasBytes ? Array.from(sasBytes) : null);
          setEncryption({ verified: true, fingerprint: null });
          setCallState(CALL_STATES.ACTIVE);
        };

        tunnel.onSasVerify = (msg) => {
          if (!mountedRef.current) return;
          setPeerSasVerified(msg.verified);
        };

        tunnel.onPeerLeft = () => { if (mountedRef.current) endCall(); };

        tunnel.onPeerReconnecting = () => {
          console.log('[TUNNEL] Connection lost — reconnecting');
        };

        tunnel.onRekeyed = (sasBytes) => {
          if (!mountedRef.current) return;
          // New DH after reconnect — SAS changed, previous verification is void
          setSasEmoji(sasBytes ? Array.from(sasBytes) : null);
          setPeerSasVerified(null);
        };

        tunnel.onError = (msg) => {
          if (!mountedRef.current) return;
          setError(msg);
          setCallState(CALL_STATES.ENDED);
        };

        await tunnel.joinAsGuest(creds.tunnel_token);
        if (mountedRef.current) setCallState(CALL_STATES.WAITING);

        // Notify creator
        api.roomNotify(code).catch(() => {});
        return;
      }

      // ─── Standard SIP mode ──────────────────────────────────────
      // Get microphone BEFORE SIP registration (requires user gesture on mobile)
      let localStream;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr) {
        if (!mountedRef.current) return;
        setError('Нет доступа к микрофону. Разрешите доступ и попробуйте снова.');
        setCallState(CALL_STATES.ENDED);
        return;
      }
      if (!mountedRef.current) { localStream.getTracks().forEach(t => t.stop()); return; }

      setCallState(CALL_STATES.REGISTERING);

      const ua = sipService.connect({
        extension: creds.guest_ext,
        password: creds.guest_pass,
        // The server names both; an older one names only the direct port.
        wsUrls: [creds.sip_ws, creds.sip_ws_fallback].filter(Boolean),
        domain: creds.sip_domain,
      });

      // Timeout: if registration doesn't happen in 15s, show error
      const regTimeout = setTimeout(() => {
        if (!mountedRef.current) return;
        console.error('[JOIN] Registration timeout');
        setError('Сервер не отвечает. Проверьте соединение и попробуйте снова.');
        setCallState(CALL_STATES.ENDED);
        sipService.disconnect();
      }, 15000);

      ua.on('connected', () => {
        console.log('[JOIN] WebSocket connected to Asterisk');
      });

      ua.on('disconnected', (e) => {
        console.error('[JOIN] WebSocket disconnected:', e?.reason || e?.code || '');
        clearTimeout(regTimeout);
        if (!mountedRef.current) return;
        if (callState === CALL_STATES.REGISTERING) {
          setError('Не удалось подключиться к серверу звонков');
          setCallState(CALL_STATES.ENDED);
        }
      });

      ua.on('registered', () => {
        clearTimeout(regTimeout);
        if (!mountedRef.current) return;
        console.log('[JOIN] SIP registered, placing call to room', creds.room_number);
        setCallState(CALL_STATES.WAITING);

        // Build ICE servers with TURN credentials from API
        const iceServers = [
          { urls: creds.stun || `stun:${creds.sip_domain}:3478` },
        ];
        if (creds.turn) {
          iceServers.push({
            urls: creds.turn.urls,
            username: creds.turn.username,
            credential: creds.turn.credential,
          });
        }
        const guestTransportPolicy = creds.privacy_mode >= 1 ? 'relay' : 'all';
        console.log('[ICE] Using servers:', JSON.stringify(iceServers), 'policy:', guestTransportPolicy);

        // Call the ConfBridge room (guest joins 7XXXX)
        try {
        sipService.call({
          extension: String(creds.room_number),
          domain: creds.sip_domain,
          localStream,
          iceServers,
          iceTransportPolicy: guestTransportPolicy,
          onStream: (remoteStream) => {
            if (!mountedRef.current) return;
            remoteStreamRef.current = remoteStream;
            const tracks = remoteStream.getAudioTracks();
            console.log('[AUDIO] Remote stream received, tracks:', tracks.length);

            if (audioRef.current) {
              const audio = audioRef.current;
              audio.srcObject = remoteStream;
              audio.play()
                .then(() => console.log('[AUDIO] play() OK'))
                .catch((err) => console.error('[AUDIO] play() failed:', err.name, err.message));
            }
            startTimer();
            verifyEncryption();
          },
          onEnded: () => endCall(),
          onFailed: (e) => {
            console.error('[JOIN] Call failed:', e?.cause);
            if (!mountedRef.current) return;
            setError(e?.cause === 'Rejected' ? 'Вызов отклонён' : 'Не удалось подключиться к звонку');
            setCallState(CALL_STATES.ENDED);
          },
        });
        console.log('[JOIN] sipService.call() succeeded');
        } catch (callErr) {
          console.error('[JOIN] sipService.call() threw:', callErr);
          if (mountedRef.current) {
            setError('Ошибка вызова: ' + callErr.message);
            setCallState(CALL_STATES.ENDED);
          }
          return;
        }

        // Notify the creator that someone is waiting
        api.roomNotify(code).catch(() => {});
      });

      ua.on('registrationFailed', (e) => {
        clearTimeout(regTimeout);
        if (!mountedRef.current) return;
        console.error('[JOIN] Registration failed:', e?.cause || '');
        setError('Ошибка регистрации на сервере');
        setCallState(CALL_STATES.ENDED);
      });

      ua.start();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message || 'Ошибка подключения');
      setCallState(CALL_STATES.ENDED);
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (tunnelRef.current) {
      tunnelRef.current.setMuted(next);
    } else {
      sipService.mute(next);
    }
  };

  const toggleSpeaker = useCallback(async () => {
    const next = !speaker;
    setSpeaker(next);

    // Native iOS: use native audio routing
    if (isNative) {
      window.VoxioxNative.setSpeaker(next);
      return;
    }

    const stream = remoteStreamRef.current;
    if (!stream) return;

    if (next) {
      // === ГРОМКАЯ СВЯЗЬ ===
      // Стратегия 1 (Chrome/Android): setSinkId
      if (audioRef.current?.setSinkId) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const speakerDev = devices.find(d => d.kind === 'audiooutput' && /speaker/i.test(d.label));
          if (speakerDev) {
            await audioRef.current.setSinkId(speakerDev.deviceId);
            console.log('[SPEAKER] setSinkId OK:', speakerDev.label);
            return;
          }
        } catch (e) { console.warn('[SPEAKER] setSinkId failed:', e); }
      }
      // Стратегия 2 (fallback): AudioContext — переключает audio session на playback (динамик)
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') await ctx.resume();
        if (sourceNodeRef.current) {
          try { sourceNodeRef.current.disconnect(); } catch (e) {}
        }
        sourceNodeRef.current = ctx.createMediaStreamSource(stream);
        sourceNodeRef.current.connect(ctx.destination);
        if (audioRef.current) audioRef.current.volume = 0;
        console.log('[SPEAKER] AudioContext route OK');
      } catch (e) {
        console.warn('[SPEAKER] AudioContext fallback failed:', e);
      }
    } else {
      // === ТЕЛЕФОННЫЙ ДИНАМИК ===
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.disconnect(); } catch (e) {}
        sourceNodeRef.current = null;
      }
      if (audioRef.current?.setSinkId) {
        try { await audioRef.current.setSinkId(''); } catch (e) {}
      }
      if (audioRef.current) {
        audioRef.current.volume = 1.0;
        audioRef.current.srcObject = stream;
        audioRef.current.play().catch(() => {});
      }
      console.log('[SPEAKER] Earpiece mode restored');
    }
  }, [speaker, isNative]);

  const fmtDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ─── Loading & error states ─────────────────────────────────────────────────
  if (loading) return <Splash text="Загрузка..." />;
  if (error && callState === CALL_STATES.IDLE) {
    return <Splash text={error} error onRetry={() => window.location.reload()} />;
  }

  // ─── Call in progress ───────────────────────────────────────────────────────
  const stateLabel = {
    [CALL_STATES.CONNECTING]: 'Получение данных...',
    [CALL_STATES.REGISTERING]: 'Подключение к серверу...',
    [CALL_STATES.WAITING]: 'Ожидание создателя...',
    [CALL_STATES.VERIFYING]: 'Проверка шифрования...',
    [CALL_STATES.ACTIVE]: fmtDuration(duration),
    [CALL_STATES.ENDED]: 'Звонок завершён',
  }[callState] || 'Подключение...';

  const isWaiting = callState === CALL_STATES.WAITING;
  const isVerifying = callState === CALL_STATES.VERIFYING;
  const isActive = callState === CALL_STATES.ACTIVE;
  const isEnded = callState === CALL_STATES.ENDED;
  const showHangup = callState !== CALL_STATES.IDLE && callState !== CALL_STATES.ENDED;

  return (
    <div style={styles.container}>
      <audio ref={(el) => { audioRef.current = el; if (el) { el.volume = 1.0; console.log('[AUDIO] <audio> element mounted'); } }} autoPlay playsInline />
      <div style={styles.callCard}>
        {/* Logo */}
        <div style={styles.logo}>
          <Diamond size={20} color={colors.gray900} strokeWidth={2} />
          <span style={styles.logoText}>Voxiox</span>
        </div>

        {/* Avatar */}
        <div style={styles.avatarWrap}>
          <div style={{
            ...styles.avatarRing,
            ...(isWaiting ? styles.avatarRingWaiting : {}),
            ...(isVerifying ? styles.avatarRingVerifying : {}),
            ...(isActive ? styles.avatarRingActive : {}),
          }} />
          <div style={styles.avatar}>
            {isVerifying ? <Shield size={40} color={colors.gray900} strokeWidth={1.5} /> : isActive && encryption?.verified ? <Lock size={40} color={colors.green} strokeWidth={1.5} /> : <Phone size={40} color={colors.gray400} strokeWidth={1.5} />}
          </div>
        </div>

        {/* Creator name */}
        <div style={styles.callName}>{linkInfo?.creator || 'Абонент'}</div>
        {linkInfo?.label && <div style={styles.callLabel}>{linkInfo.label}</div>}

        {/* Link TTL */}
        {linkInfo?.expires_at && !isActive && !isEnded && (
          <div style={styles.ttlBadge}>
            <Clock size={14} style={{marginRight:4, verticalAlign:'middle'}} /> Действует до {(() => { const s = String(linkInfo.expires_at); const d = s.endsWith('Z') || s.includes('+') ? new Date(s) : new Date(s.replace(' ', 'T') + 'Z'); return d.toLocaleString('ru-RU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); })()}
          </div>
        )}

        {/* Status */}
        <div style={{
          ...styles.callStatus,
          color: isActive ? colors.green : isVerifying ? colors.gray900 : isEnded ? colors.gray400 : colors.gray500,
        }}>
          {stateLabel}
        </div>

        {/* Encryption verification steps */}
        {isVerifying && (
          <div style={styles.verifyBox}>
            {verifySteps.map((step, i) => (
              <div key={i} style={styles.verifyStep}>
                <span style={{ color: step.done ? colors.green : colors.gray400, marginRight: 8, display: 'flex', alignItems: 'center' }}>
                  {step.done ? <Check size={14} /> : '○'}
                </span>
                <span style={{ color: step.done ? colors.gray900 : colors.gray400, fontSize: 13 }}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Active call — encryption badge (non-tunnel) */}
        {isActive && encryption?.verified && !sasEmoji && (
          <div style={styles.encryptionBadge}>
            <span style={{ color: colors.green, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Lock size={14} color={colors.green} style={{marginRight:4}} /> Зашифровано end-to-end
            </span>
            {encryption.fingerprint && (
              <div style={styles.fingerprint}>
                SHA-256: {encryption.fingerprint.slice(0, 23)}...
              </div>
            )}
          </div>
        )}

        {/* E2EE SAS interactive verification */}
        {isActive && sasEmoji && (
          <SASVerification
            sasEmoji={sasEmoji}
            sasVerified={sasVerified}
            mySasChoice={mySasChoice}
            peerSasVerified={peerSasVerified}
            authState={authState}
            onConfirm={handleSasConfirm}
            onDeny={handleSasDeny}
          />
        )}

        {/* Error */}
        {error && <div style={styles.callError}>{error}</div>}

        {/* Controls */}
        <div style={styles.callControls}>
          {isActive && (
            <>
            <button onClick={toggleMute} style={{ ...styles.ctrlBtn, ...(muted ? styles.ctrlBtnActive : {}) }}>
              {muted ? <MicOff size={24} /> : <Mic size={24} />}
              <span style={styles.ctrlLabel}>{muted ? 'Вкл.' : 'Выкл.'}</span>
            </button>
            <button onClick={toggleSpeaker} style={{ ...styles.ctrlBtn, ...(speaker ? styles.ctrlBtnActive : {}) }}>
              {speaker ? <Volume2 size={24} /> : <Volume1 size={24} />}
              <span style={styles.ctrlLabel}>Динамик</span>
            </button>
            </>
          )}
          {showHangup && (
            <button onClick={endCall} style={styles.hangupBtn}>
              <X size={24} />
              <span style={styles.ctrlLabel}>{isActive || isVerifying ? 'Завершить' : 'Отмена'}</span>
            </button>
          )}
          {isEnded && (
            <button onClick={() => window.location.reload()} style={styles.retryBtn}>
              Перезвонить
            </button>
          )}
        </div>
      </div>

      {/* Version */}
      <div style={{ position: 'fixed', bottom: 8, right: 12, fontSize: 10, color: colors.gray300, fontFamily: 'monospace' }}>
        v{APP_VERSION} · {BUILD_DATE}
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { transform:scale(1); opacity:.3 } 50% { transform:scale(1.1); opacity:.7 } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 8px rgba(21,20,26,0.3) } 50% { box-shadow: 0 0 20px rgba(21,20,26,0.6) } }
        @keyframes pulseGreen { 0%,100% { box-shadow: 0 0 8px rgba(22,163,74,0.2) } 50% { box-shadow: 0 0 16px rgba(22,163,74,0.5) } }
      `}</style>
    </div>
  );
}

// ─── SAS Emoji Map ──────────────────────────────────────────


// ADR-014: what the identity-key check concluded about the peer.
const AUTH_COPY = {
  verified:     { text: 'Собеседник подтверждён ранее', tone: 'ok' },
  pinned:       { text: 'Ключ собеседника известен', tone: 'ok' },
  'tofu-new':   { text: 'Первый звонок с этим собеседником — сверьте эмодзи', tone: 'neutral' },
  'new-device': { text: 'Новое устройство собеседника — обязательно сверьте эмодзи', tone: 'warn' },
  anonymous:    { text: 'Собеседник не аутентифицирован (гость)', tone: 'neutral' },
};

function AuthBadge({ authState }) {
  const copy = AUTH_COPY[authState];
  if (!copy) return null;
  const color = copy.tone === 'ok' ? colors.green : copy.tone === 'warn' ? colors.amber : colors.gray500;
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
      color, marginBottom: 10, lineHeight: 1.4,
    }}>
      {copy.text}
    </div>
  );
}

function SASVerification({ sasEmoji, sasVerified, mySasChoice, peerSasVerified, authState, onConfirm, onDeny }) {
  const emojis = sasToEmoji(sasEmoji) || [];
  const isRejected = sasVerified === false;
  const isBothConfirmed = sasVerified === true;
  const isPending = mySasChoice === null;
  const isWaitingPeer = mySasChoice === true && peerSasVerified === null;
  const bgColor = isRejected ? 'rgba(221,122,92,0.06)' : isBothConfirmed ? 'rgba(79,182,140,0.06)' : 'transparent';
  const borderColor = isRejected ? 'rgba(221,122,92,0.3)' : isBothConfirmed ? 'rgba(79,182,140,0.3)' : colors.gray200;

  return (
    <div style={{
      background: bgColor, border: `1px solid ${borderColor}`,
      borderRadius: radii.lg, padding: '16px 20px', width: '100%',
      textAlign: 'center', transition: 'all 0.3s',
    }}>
      <div style={{ marginBottom: 8 }}>
        {isRejected ? <ShieldAlert size={28} color={colors.red} /> : isBothConfirmed ? <ShieldCheck size={28} color={colors.green} /> : <Shield size={28} color={colors.gray400} />}
      </div>
      <AuthBadge authState={authState} />
      <div style={{ fontSize: 36, letterSpacing: 8, marginBottom: 12, lineHeight: 1.4 }}>
        {emojis.join(' ')}
      </div>
      {isRejected && <div style={{ fontSize: 13, color: colors.red, fontWeight: 600, marginBottom: 8 }}>⚠ Иконки не совпали. Возможна MITM-атака.</div>}
      {isBothConfirmed && <div style={{ fontSize: 13, color: colors.green, fontWeight: 600, marginBottom: 8 }}>✓ Соединение верифицировано</div>}
      {isPending && <div style={{ fontSize: 12, color: colors.gray500, marginBottom: 12, lineHeight: 1.5 }}>Собеседник вас слышит. Сверьте иконки по голосу и подтвердите, что они совпадают.</div>}
      {isWaitingPeer && <div style={{ fontSize: 12, color: colors.gray500, marginBottom: 8 }}>Ожидание подтверждения собеседника...</div>}
      {isPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={onConfirm} style={{ flex: 1, background: colors.greenDot, border: 'none', color: colors.gray50, padding: '10px 16px', borderRadius: radii.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>✓ Совпадают</button>
          <button onClick={onDeny} style={{ flex: 1, background: colors.gray100, border: `1px solid ${colors.gray200}`, color: colors.gray600, padding: '10px 16px', borderRadius: radii.sm, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}>✗ Не совпадают</button>
        </div>
      )}
    </div>
  );
}

function Splash({ text, error, onRetry }) {
  return (
    <div style={{ minHeight: '100vh', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, flexDirection: 'column', gap: 16 }}>
      <div style={{ color: error ? colors.red : colors.gray400, fontSize: 15 }}>{text}</div>
      {error && onRetry && <button onClick={onRetry} style={{ background: colors.gray900, color: colors.gray50, border: 'none', padding: '10px 24px', borderRadius: radii.lg, fontSize: 14, cursor: 'pointer' }}>Попробовать снова</button>}
    </div>
  );
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
    padding: 16,
  },
  callCard: {
    width: '100%', maxWidth: 380,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    background: colors.white, borderRadius: radii.lg, padding: '40px 32px',
    boxShadow: shadows.sm,
    border: `1px solid ${colors.gray100}`,
  },
  logo: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  logoText: { fontSize: 18, fontWeight: 600, color: colors.gray900 },
  avatarWrap: {
    position: 'relative', width: 100, height: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  avatarRing: {
    position: 'absolute', width: '100%', height: '100%', borderRadius: '50%',
    border: `2px solid ${colors.gray200}`, transition: 'all 0.5s',
  },
  avatarRingWaiting: {
    animation: 'pulse 2s infinite', borderColor: colors.amber,
  },
  avatarRingVerifying: {
    animation: 'glow 1.5s infinite', borderColor: colors.gray900,
  },
  avatarRingActive: {
    animation: 'pulseGreen 2s infinite', borderColor: colors.green,
  },
  avatar: { display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 },
  callName: { fontSize: 24, fontWeight: 600, color: colors.gray900, fontFamily: "'New York', 'Iowan Old Style', 'Palatino', Georgia, serif" },
  callLabel: { fontSize: 13, color: colors.gray500, marginTop: -4 },
  callStatus: { fontSize: 14, fontWeight: 500 },
  verifyBox: {
    background: colors.gray100, borderRadius: radii.md, padding: '12px 16px',
    width: '100%', display: 'flex', flexDirection: 'column', gap: 6,
  },
  verifyStep: { display: 'flex', alignItems: 'center' },
  encryptionBadge: {
    background: colors.greenLight, border: `1px solid ${colors.greenBorder}`,
    borderRadius: radii.md, padding: '8px 14px', textAlign: 'center',
  },
  fingerprint: {
    fontSize: 10, color: colors.gray500, fontFamily: 'monospace',
    marginTop: 4, letterSpacing: 0.5,
  },
  callError: { fontSize: 13, color: colors.red, background: colors.redLight, padding: '8px 16px', borderRadius: radii.sm },
  callControls: { display: 'flex', gap: 16, marginTop: 8 },
  ctrlBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: colors.gray100, border: 'none',
    borderRadius: radii.lg, width: 72, height: 72, cursor: 'pointer',
    color: colors.gray600, fontSize: 24, justifyContent: 'center',
  },
  ctrlBtnActive: { background: colors.gray900, color: colors.gray50 },
  ctrlLabel: { fontSize: 9, fontWeight: 500 },
  hangupBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: colors.red, border: 'none',
    borderRadius: radii.lg, width: 72, height: 72, cursor: 'pointer',
    color: colors.gray50, fontSize: 24, justifyContent: 'center',
  },
  ttlBadge: {
    fontSize: 12, color: colors.gray500,
    background: colors.gray100, borderRadius: radii.sm,
    padding: '6px 12px',
  },
  retryBtn: {
    background: colors.gray900, border: 'none',
    color: colors.gray50, padding: '12px 28px', borderRadius: radii.lg,
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
